// Imports => React
import React, { useEffect, useState, useMemo } from 'react';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react-lite';
import { withRouter } from 'react-router-dom';
import { Fade } from 'react-reveal';
import clsx from 'clsx';

// Imports => Constants
import { KEYS, ROUTES, THEMES, TITLES, TYPES } from '@constants';

// Imports => Utilities
import {
	AcUUID,
	AcIsEmail,
	AcIsEmptyString,
	AcIsSet,
	AcIsNull,
	AcSupportsWEBP,
} from '@utils';

// Imports => Atoms
import { AcContainer, AcRow, AcColumn } from '@atoms/ac-grid';
import AcBottomDrawer from '@atoms/ac-bottom-drawer/ac-bottom-drawer.web';
import AcHeading from '@atoms/ac-heading/ac-heading.web.js';
import AcRichContent from '@atoms/ac-rich-content/ac-rich-content.web';
import AcTextInput from '@atoms/ac-text-input/ac-text-input.web';
import AcButton from '@atoms/ac-button/ac-button.web';
import AcBackButton from '@atoms/ac-back-button/ac-back-button.web';

const _CLASSES = {
	MAIN: 'ac-page ac-login',
	LOGO: {
		MAIN: 'ac-login__logo',
		WRP: 'ac-login__logo-wrp',
	},
};

const AcAccountSelect = ({ history, store }) => {
	const { is_loading, current_email, loaded } = store.auth;

	const input_id = AcUUID();
	const button_id = AcUUID();

	const [email, setEmail] = useState(current_email || '');
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [errors, setErrors] = useState({
		[KEYS.EMAIL]: current_email ? null : undefined,
	});

	const hasErrors = useMemo(() => {
		const result = !AcIsNull(errors[KEYS.EMAIL]);
		return result;
	}, [email, errors]);

	const showDrawer = () => {
		const $input = document.getElementById(input_id);

		if ($input && $input === document.activeElement) return;

		if (!hasErrors) {
			setDrawerVisible(true);
		}
	};

	const hideDrawer = () => {
		setDrawerVisible(false);
	};

	useEffect(() => {
		if (store.auth.current_email && !AcIsNull(errors[KEYS.EMAIL]))
			setDrawerVisible(true);
	}, []);

	const handleFormSubmit = async event => {
		if (event && event.preventDefault) event.preventDefault();
		if (hasErrors) return;

		await store.auth.set(KEYS.EMAIL, email);

		const { replace } = history;
		if (replace && !hasErrors) {
			replace(ROUTES.LOGIN.path);
		}
	};

	const handleInputValidation = (name, value, type) => {
		let result = errors;

		switch (name) {
			case KEYS.EMAIL:
				if (!AcIsSet(value) || AcIsEmptyString(value)) {
					result[name] = 'E-mailadres is een verplicht veld';
				} else if (!AcIsEmail(value)) {
					result[name] = 'Dit is geen geldig e-mailadres';
				} else {
					result[name] = null;
				}
				break;
			default:
		}

		setErrors(result);

		return result[name];
	};

	const handleInputChange = (event, name, value, type) => {
		if (event && event.persist) event.persist();

		switch (name) {
			case KEYS.EMAIL:
				setEmail(value);
				break;
			default:
		}
	};

	const getEmailInputOptions = useMemo(() => {
		return {
			onBlur: showDrawer,
			id: input_id,
			placeholder: 'E-mailadres',
			type: TYPES.EMAIL,
			name: KEYS.EMAIL,
			value: email,
			focus: 'delayed',
			delay: !loaded ? 3000 : 300,
			required: true,
			disabled: is_loading,
			validation: handleInputValidation,
			autoComplete: 'new-password',
			callback: handleInputChange,
			onFocus: hideDrawer,
		};
	}, [email, is_loading, loaded]);

	const getButtonoptions = useMemo(() => {
		return {
			id: button_id,
			type: TYPES.SUBMIT,
			theme: THEMES.ALPHA,
			disabled: hasErrors || is_loading,
			loading: is_loading,
			title: 'Bevestig',
			callback: handleFormSubmit,
		};
	}, [email, hasErrors, is_loading]);

	const getMainClassNames = useMemo(() => {
		return clsx(_CLASSES.MAIN);
	}, []);

	const getLogoWrpClassNames = useMemo(() => {
		return clsx(_CLASSES.LOGO.WRP);
	}, []);

	const getLogoClassNames = useMemo(() => {
		return clsx(_CLASSES.LOGO.MAIN);
	}, []);

	const renderLogo = useMemo(() => {
		const source = AcSupportsWEBP()
			? 'vvd-logo-02b-1x.webp'
			: 'vvd-logo-02b-1x.png';
		const img = require(`@assets/images/${source}`);

		return (
			<div className={getLogoWrpClassNames}>
				<img src={img} className={getLogoClassNames} alt={'VVD logo 02b 2x'} />
			</div>
		);
	}, [getLogoWrpClassNames, getLogoClassNames]);

	return (
		<div className={getMainClassNames}>
			{history && history.action === 'PUSH' && (
				<AcBackButton theme={THEMES.LIGHT} />
			)}

			{renderLogo}

			<form method={'post'} onSubmit={handleFormSubmit}>
				<Fade duration={150}>
					<AcContainer className={'h-text--align-center'}>
						<AcRow>
							<AcColumn>
								<AcHeading rank={3}>{TITLES.LOGIN}</AcHeading>
							</AcColumn>
						</AcRow>

						<AcRow>
							<AcColumn>
								<AcRichContent content={`<p>Vul je e-mailadres in</p>`} />
							</AcColumn>
						</AcRow>

						<AcRow className={'h-margin-top-5'}>
							<AcColumn>
								<AcTextInput {...getEmailInputOptions} />
							</AcColumn>
						</AcRow>
					</AcContainer>
				</Fade>

				<AcBottomDrawer visible={drawerVisible}>
					<AcContainer fluid className={'h-text--align-center'}>
						<AcRow>
							<AcColumn>
								<AcButton {...getButtonoptions}>
									<span>Bevestig</span>
								</AcButton>
							</AcColumn>
						</AcRow>
					</AcContainer>
				</AcBottomDrawer>
			</form>
		</div>
	);
};

export default withRouter(inject('store')(observer(AcAccountSelect)));
